import React, { useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import { RectangleStackIcon, CheckIcon } from "@heroicons/react/24/outline";
import { MultiSSOLogin } from "./MultiSSOLogin";

const WorkspaceCard = ({ workspace, isSelected = false, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
          group relative aspect-square h-40 w-40 cursor-pointer rounded-xl border bg-white p-4 shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-lg
          ${isSelected ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300"}
        `}>
      {isSelected && (
        <div className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-white shadow-lg ring-2 ring-white">
          <CheckIcon className="h-3.5 w-3.5" />
        </div>
      )}

      <div className="flex h-full flex-col items-center justify-center space-y-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 transition-transform duration-200 group-hover:scale-110">
          {workspace.square_logo || workspace.image_favicon || workspace.image_logo ? (
            <img
              src={workspace.square_logo || workspace.image_favicon || workspace.image_logo}
              alt={workspace.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <RectangleStackIcon className="h-7 w-7 text-gray-400" />
          )}
        </div>
        <div className="w-full px-1">
          <h3 className="line-clamp-2 text-center text-sm font-medium text-gray-900 transition-colors duration-200 group-hover:text-blue-600">{workspace.name}</h3>
        </div>
      </div>
    </button>
  );
};

const WorkspaceSelector = ({ workspaces = [], handleBackToLogin, onSSOSelect, className = "", renderPasswordOption }) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [showSSOSelection, setShowSSOSelection] = useState(false);

  const handleWorkspaceSelect = (workspace) => {
    const dualLogin = !workspace?.dual_signin_for_user && workspace?.ssos?.length === 1;

    if (dualLogin) {
      const ssoProvider = workspace.ssos[0];
      onSSOSelect?.(ssoProvider, workspace);
    } else {
      setSelectedWorkspaceId(workspace._id);
      setShowSSOSelection(true);
    }
  };

  const handleBackToWorkspaceSelect = () => {
    setShowSSOSelection(false);
    setSelectedWorkspaceId(null);
  };

  const handleSSOProviderSelect = (ssoProvider) => {
    const selectedWorkspace = workspaces.find((w) => w._id === selectedWorkspaceId);
    onSSOSelect?.(ssoProvider, selectedWorkspace);
  };

  const selectedWorkspace = workspaces.find((w) => w._id === selectedWorkspaceId);

  if (showSSOSelection && selectedWorkspace) {
    return (
      <MultiSSOLogin
        workspace={selectedWorkspace}
        sso={selectedWorkspace.ssos || []}
        onSSOSelect={handleSSOProviderSelect}
        onBack={handleBackToWorkspaceSelect}
        showBackButton={true}
        renderPasswordOption={renderPasswordOption}
      />
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="my-6 w-full items-center justify-center space-y-3">
        {workspaces?.length > 1 && <h2 className="text-center text-xl font-semibold text-gray-900">Choose a workspace to login</h2>}
        <button
          onClick={handleBackToLogin}
          className="mx-auto flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors">
          Return to <span className="text-highlightColor hover:underline">Login Page</span>
        </button>
      </div>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-center gap-4">
          {workspaces?.length === 1 ? (
            <MultiSSOLogin
              workspace={workspaces[0]}
              sso={workspaces[0].ssos}
              onSSOSelect={(ssoProvider) => onSSOSelect?.(ssoProvider, workspaces[0])}
              showBackButton={false}
              renderPasswordOption={renderPasswordOption}
            />
          ) : (
            workspaces?.map((workspace) => (
              <WorkspaceCard
                key={workspace._id}
                workspace={workspace}
                isSelected={workspace._id === selectedWorkspaceId}
                onClick={() => handleWorkspaceSelect(workspace)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelector;
